/**
 * AI Indicator — Pixel Grid (6x6 diamond)
 * A 6x6 grid with hard diamond mask — only 24 pixels visible.
 * States: idle → typing → thinking → idle | error | success
 *
 * Options:
 *   externalControl: true  — skips internal input/keydown listeners,
 *                             caller drives state via returned setState()
 *
 * DEV NOTE (Artur): When externalControl is false (default, used by hero search),
 * the indicator manages its own state machine from input events.
 * When externalControl is true (newsletter input), the parent code calls
 * setState('typing' | 'thinking' | 'error' | 'success' | 'idle') directly.
 *
 * Grid layout (6x6, indices 0-35):
 *  .  .  2  3  .  .
 *  .  7  8  9 10  .
 * 12 13 14 15 16 17
 * 18 19 20 21 22 23
 *  . 25 26 27 28  .
 *  .  . 32 33  .  .
 */

const GRID = 7;
const PX = 4;
const GAP = 1;
const STEP = PX + GAP;
const COLORS = ['#5bb8ff', '#c084fc', '#ff7b6b', '#60a5fa', '#a855f7'];
const ERROR_COLORS = ['#ff6b6b', '#ff8a65', '#e57373', '#ff5252', '#ef5350'];
const SUCCESS_COLORS = ['#4ade80', '#22c55e', '#86efac', '#34d399', '#10b981'];

// 7x7 diamond — 25 visible pixels
//  .  .  .  3  .  .  .
//  .  .  9 10 11  .  .
//  . 15 16 17 18 19  .
// 21 22 23 24 25 26 27
//  . 29 30 31 32 33  .
//  .  . 37 38 39  .  .
//  .  .  . 45  .  .  .
const VISIBLE = new Set([3, 9,10,11, 15,16,17,18,19, 21,22,23,24,25,26,27, 29,30,31,32,33, 37,38,39, 45]);

// Checkmark shape (drawn sequentially on success)
const CHECK_PIXELS = [32, 18, 30, 31, 25, 16, 23, 17];

// X shape (drawn sequentially on error)
const ERROR_PIXELS = [18, 32, 30, 16, 24, 26, 22];

// Ready shape (shown when valid email entered, waiting for Enter)
const READY_PIXELS = [17, 24, 31, 23, 25];
const READY_COLORS = ['#93c5fd', '#a5b4fc', '#c4b5fd', '#93c5fd', '#818cf8'];

const SHAPE_BG_MAX = 0.02;

function initPixelIndicator(container, inputEl, thinkingEl = null, options = {}) {
  const W = GRID * STEP;
  const H = GRID * STEP;
  const canvas = document.createElement('canvas');
  canvas.width = W * 2;
  canvas.height = H * 2;
  canvas.style.cssText = `width:${W}px;height:${H}px;`;
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  ctx.scale(2, 2);

  let state = 'idle';
  let typingTimer = null;
  let thinkingTimer = null;
  let thinkingStart = 0;

  const grid = Array.from({ length: GRID * GRID }, () => ({
    brightness: 0, targetBright: 0, color: 0, phase: Math.random() * Math.PI * 2,
  }));

  // Ripple system
  const ripples = [];
  const sparkles = [];

  const spawnRipple = () => {
    ripples.push({
      ox: Math.random(), oy: Math.random(),
      t: 0,
      speed: 3 + Math.random() * 8,
      decay: 0.6 + Math.random() * 1.2,
      colorShift: Math.random() * COLORS.length,
    });
    if (ripples.length > 6) ripples.shift();
    const count = 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      sparkles.push({
        idx: Math.floor(Math.random() * GRID * GRID),
        t: 0, duration: 0.15 + Math.random() * 0.25,
        color: Math.floor(Math.random() * COLORS.length),
      });
    }
    if (sparkles.length > 12) sparkles.splice(0, sparkles.length - 12);
  };

  let currentLabel = '';
  const showLabel = (v, text, isError) => {
    if (!thinkingEl) return;
    if (!v) { thinkingEl.classList.remove('visible'); thinkingEl.classList.remove('error'); return; }
    if (text && text !== currentLabel && thinkingEl.classList.contains('visible')) {
      thinkingEl.classList.remove('visible');
      setTimeout(() => {
        thinkingEl.textContent = text; currentLabel = text;
        thinkingEl.classList.toggle('error', !!isError);
        thinkingEl.classList.add('visible');
      }, 400);
    } else {
      if (text) { thinkingEl.textContent = text; currentLabel = text; }
      thinkingEl.classList.toggle('error', !!isError);
      thinkingEl.classList.add('visible');
    }
  };

  // Input events — only when NOT externally controlled
  if (!options.externalControl) {
    inputEl.addEventListener('input', () => {
      if (inputEl.value.length > 0) {
        state = 'typing';
        showLabel(false);
        clearTimeout(typingTimer);
        clearTimeout(thinkingTimer);
        spawnRipple();
        typingTimer = setTimeout(() => {
          if (state === 'typing') {
            state = 'thinking';
            thinkingStart = t;
            showLabel(true, 'thinking...');
            thinkingTimer = setTimeout(() => {
              if (state !== 'thinking') return;
              if (inputEl.value.trim().toLowerCase() === 'error') {
                state = 'error';
                showLabel(true, 'no luck, try again...', true);
              } else {
                state = 'idle';
                showLabel(false);
              }
            }, 4000);
          }
        }, 1200);
      } else {
        state = 'idle';
        showLabel(false);
        clearTimeout(typingTimer);
        clearTimeout(thinkingTimer);
      }
    });

    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && inputEl.value.length > 0) {
        state = 'searching';
        showLabel(false);
        clearTimeout(typingTimer);
        clearTimeout(thinkingTimer);
        setTimeout(() => { state = 'idle'; }, 3000);
      }
      if (state === 'typing' && e.key.length === 1) spawnRipple();
    });
  }

  let paused = false;
  document.addEventListener('visibilitychange', () => { paused = document.hidden; });

  let t = 0;
  function animate() {
    requestAnimationFrame(animate);
    if (paused) return;
    t += 0.016;
    ctx.clearRect(0, 0, W, H);

    for (let i = 0; i < GRID * GRID; i++) {
      if (!VISIBLE.has(i)) continue;

      const px = grid[i];
      const col = i % GRID;
      const row = Math.floor(i / GRID);
      const x = col * STEP;
      const y = row * STEP;
      const nx = col / (GRID - 1);
      const ny = row / (GRID - 1);

      if (state === 'idle') {
        px.targetBright = 0.65 + 1.0 * Math.sin(t * 0.8 + px.phase) + 0.2 * Math.sin(t * 1.2 + nx * 3 + ny * 2);
        px.color = Math.floor((t * 0.3 + nx + ny) % COLORS.length);

      } else if (state === 'typing') {
        let bright = 0.3;
        for (let r = 0; r < ripples.length; r++) {
          const rp = ripples[r];
          const rdist = Math.sqrt((nx - rp.ox) ** 2 + (ny - rp.oy) ** 2);
          const wave = Math.sin(rp.t * rp.speed - rdist * 12) * 0.5 + 0.5;
          const envelope = Math.max(0, 1 - rp.t * rp.decay);
          bright += wave * envelope * 0.7;
        }
        for (let s = 0; s < sparkles.length; s++) {
          if (sparkles[s].idx === i) {
            const sp = sparkles[s];
            const flash = 1 - sp.t / sp.duration;
            if (flash > 0) { bright = Math.max(bright, flash * 1.2); px.color = sp.color; }
          }
        }
        px.targetBright = Math.min(bright, 1.2);
        px.color = Math.floor((t * 2 + nx * 3 + ny * 2 + (ripples.length > 0 ? ripples[ripples.length - 1].colorShift : 0)) % COLORS.length);

      } else if (state === 'thinking') {
        const elapsed = t - thinkingStart;
        const dist = Math.sqrt((nx - 0.5) ** 2 + (ny - 0.5) ** 2);

        let bright = 0;
        for (let r = 0; r < 2; r++) {
          const rippleT = (elapsed * 0.9 + r * 0.7) % 1.8;
          const ringRadius = rippleT * 0.7;
          const ring = Math.max(0, 1 - Math.abs(dist - ringRadius) / 0.18);
          const fade = Math.max(0, 1 - rippleT / 1.4);
          bright += ring * fade;
        }
        bright += Math.max(0, 1 - dist * 6) * (0.6 + 0.3 * Math.sin(elapsed * 2));

        px.targetBright = Math.min(bright, 1.2);
        px.color = Math.floor((elapsed * 2.5 + dist * 3) % COLORS.length);

      } else if (state === 'searching') {
        const angle = Math.atan2(ny - 0.5, nx - 0.5);
        px.targetBright = 0.6 + 0.4 * Math.sin(t * 6 + angle * 3);
        px.color = Math.floor((t * 3 + angle) % COLORS.length);

      } else if (state === 'error') {
        const elapsed = t - thinkingStart;
        const errIdx = ERROR_PIXELS.indexOf(i);

        if (errIdx >= 0) {
          const drawTime = 0.15 + errIdx * 0.08;
          const fadeIn = Math.min(1, Math.max(0, (elapsed - drawTime) / 0.2));
          px.targetBright = fadeIn * (0.85 + 0.15 * Math.sin(t * 1.5 + px.phase));
        } else {
          px.targetBright = SHAPE_BG_MAX * (0.5 + 0.5 * Math.sin(t * 0.8 + px.phase));
        }
        px.color = Math.floor(px.phase * 3) % ERROR_COLORS.length;

      } else if (state === 'success') {
        const elapsed = t - thinkingStart;
        const checkIdx = CHECK_PIXELS.indexOf(i);

        if (checkIdx >= 0) {
          const drawTime = 0.2 + checkIdx * 0.12;
          const fadeIn = Math.min(1, Math.max(0, (elapsed - drawTime) / 0.25));
          px.targetBright = fadeIn * (0.9 + 0.1 * Math.sin(t * 2 + px.phase));
        } else {
          px.targetBright = SHAPE_BG_MAX * (0.5 + 0.5 * Math.sin(t * 1.2 + px.phase));
        }
        px.color = Math.floor((t * 0.5 + nx) % SUCCESS_COLORS.length);

      } else if (state === 'ready') {
        const elapsed = t - thinkingStart;
        const readyIdx = READY_PIXELS.indexOf(i);

        if (readyIdx >= 0) {
          const drawTime = 0.15 + readyIdx * 0.08;
          const fadeIn = Math.min(1, Math.max(0, (elapsed - drawTime) / 0.25));
          px.targetBright = fadeIn * (0.8 + 0.2 * Math.sin(t * 1.5 + px.phase));
        } else {
          px.targetBright = SHAPE_BG_MAX * (0.5 + 0.5 * Math.sin(t * 1.0 + px.phase));
        }
        px.color = Math.floor((t * 0.4 + nx) % READY_COLORS.length);
      }

      // Center pixel boost (not in shape states)
      if (state !== 'error' && state !== 'success' && state !== 'ready' && i === 24) {
        px.targetBright = Math.max(px.targetBright, 0.9);
      }

      // Radial feather — outer pixels dim into background
      const fdx = nx - 0.5, fdy = ny - 0.5;
      const fDist = Math.sqrt(fdx * fdx + fdy * fdy) * 2;
      const feather = Math.max(0, Math.min(1, 1.8 - fDist * 1.8));
      px.targetBright *= feather;

      px.brightness += (px.targetBright - px.brightness) * 0.12;
      const alpha = Math.max(0, Math.min(1, px.brightness));

      if (alpha > 0.02) {
        ctx.globalAlpha = alpha;
        const palette = state === 'error' ? ERROR_COLORS
          : state === 'success' ? SUCCESS_COLORS
          : state === 'ready' ? READY_COLORS
          : COLORS;
        ctx.fillStyle = palette[Math.abs(px.color) % palette.length];
        ctx.beginPath();
        ctx.roundRect(x, y, PX, PX, 1);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;

    // Advance ripple & sparkle timers
    const dt = 0.016;
    for (let r = ripples.length - 1; r >= 0; r--) {
      ripples[r].t += dt;
      if (ripples[r].t > 3) ripples.splice(r, 1);
    }
    for (let s = sparkles.length - 1; s >= 0; s--) {
      sparkles[s].t += dt;
      if (sparkles[s].t > sparkles[s].duration) sparkles.splice(s, 1);
    }
  }

  animate();
  return {
    setState: (s) => { state = s; thinkingStart = t; },
    spawnRipple,
    pause:  () => { paused = true; },
    resume: () => { paused = false; },
  };
}

window.PixelIndicator = { initPixelIndicator };
